export type Wallet = { currency: 'USD'; balance: string; used: string | null };
export type Account = {
  id: number;
  username: string;
  name: string;
  email: string;
  status: number;
  role: number;
  wallet: Wallet;
  permissions?: { manageClients: boolean };
};
export type Page<T> = { items: T[]; page: number; pageSize: number; total: number };
export type KeySummary = {
  id: number;
  name: string;
  maskedKey: string;
  status: number;
  createdAt: number;
  lastUsedAt: number;
  expiresAt: number;
  unlimitedQuota: boolean;
  remainingUsd: string | null;
  usedUsd: string | null;
  modelLimitsEnabled: boolean;
  modelLimits: string;
  allowIps: string | null;
};
export type UsageFilters = { start?: number; end?: number; model?: string; key?: string };
export type UsageSummary = { amountUsd: string | null; quota: number; rpm: number; tpm: number };
export type UsageRecord = {
  id: number;
  createdAt: number;
  type: number;
  modelName: string;
  tokenName: string;
  requestId: string;
  promptTokens: number;
  completionTokens: number;
  durationSeconds: number;
  streaming: boolean;
  quota: number | null;
  amountUsd: string | null;
};
export type Session = { account: Account; csrfToken: string };
export type CreatedKey = { key: KeySummary; secret: string };
export type Client = {
  id: number; username: string; name: string; createdAt: number; createdBy: number | null; source: 'created' | 'existing';
};
export type ClientOperation = {
  id: string; username: string; name: string;
  state: 'prepared' | 'dispatching' | 'created_unresolved' | 'complete' | 'conflict' | 'uncertain';
  createdAt: number; updatedAt: number; clientId: number | null; attemptId: string | null;
};
export type ClientCreationInput = { operationId: string; attemptId: string; username: string; name: string; password: string };
export type ClientCreationResult = { operation: ClientOperation; client?: Client };

export interface ConsoleService {
  readSession(): Promise<Session>;
  login(username: string, password: string): Promise<Session>;
  logout(): Promise<void>;
  getAccount(): Promise<Account>;
  getWallet(): Promise<Wallet>;
  listKeys(page?: number, pageSize?: number): Promise<Page<KeySummary>>;
  createKey(name: string): Promise<CreatedKey>;
  revokeKey(id: number): Promise<void>;
  usageSummary(filters?: UsageFilters): Promise<UsageSummary>;
  usageHistory(filters?: UsageFilters, page?: number, pageSize?: number): Promise<Page<UsageRecord>>;
  listClients(page?: number, pageSize?: number): Promise<Page<Client>>;
  createClient(input: ClientCreationInput): Promise<ClientCreationResult>;
  listClientOperations(page?: number, pageSize?: number): Promise<Page<ClientOperation>>;
  getClientOperation(id: string): Promise<ClientCreationResult>;
}

const messages: Record<string, string> = {
  invalid_input: 'Check your input and try again.',
  invalid_credentials: 'The username or password is incorrect.',
  unsupported_authentication: 'This account requires an authentication method that is not available yet.',
  customer_access_denied: 'This account does not have access to the console.',
  session_required: 'Please sign in to continue.',
  session_expired: 'Your session has expired. Please sign in again.',
  csrf_rejected: 'Your security token has expired. Reload the console before trying again.',
  origin_rejected: 'The console could not verify this request. Reload the page and try again.',
  login_rate_limited: 'Too many sign-in attempts. Wait a minute before trying again.',
  unsupported_operation: 'This operation is not available.',
  upstream_rejected: 'The account service could not complete this request.',
  upstream_unavailable: 'The account service is unavailable. Please try again later.',
  upstream_timeout: 'The account service timed out. Please try again later.',
  upstream_invalid_response: 'The account service returned an invalid response.',
  upstream_response_too_large: 'The account service returned an oversized response.',
  mutation_uncertain: 'The result is uncertain. Refresh the current list before trying again.',
  key_creation_incomplete: 'The key may have been created. Refresh your key list before creating another key.',
  network_error: 'Could not reach the console service. Please try again.',
  invalid_response: 'The console service returned an invalid response.',
  request_failed: 'The console could not complete this request.',
  staff_access_denied: 'Staff access is required to manage clients.',
  username_unavailable: 'This username is unavailable. Choose another username.',
  request_conflict: 'This creation request conflicts with an earlier request. Check its status before continuing.',
  client_creation_uncertain: 'The client creation result is uncertain. Check its status before continuing.',
  provisioning_unavailable: 'Client creation is temporarily unavailable. Please try again later.',
};

export class ConsoleApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly mutationUncertain: boolean;

  constructor(code: string, status = 0) {
    const safeCode = Object.hasOwn(messages, code) ? code : 'request_failed';
    super(messages[safeCode]);
    this.name = 'ConsoleApiError';
    this.code = safeCode;
    this.status = status;
    this.mutationUncertain = ['mutation_uncertain', 'key_creation_incomplete', 'client_creation_uncertain', 'request_conflict'].includes(safeCode);
  }
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new ConsoleApiError('invalid_response');
  return value as Record<string, unknown>;
}
function text(value: unknown): string {
  if (typeof value !== 'string') throw new ConsoleApiError('invalid_response');
  return value;
}
function integer(value: unknown): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) throw new ConsoleApiError('invalid_response');
  return value;
}
function boolean(value: unknown): boolean {
  if (typeof value !== 'boolean') throw new ConsoleApiError('invalid_response');
  return value;
}
function decimal(value: unknown): string {
  if (typeof value !== 'string' || !/^-?\d+(?:\.\d+)?$/.test(value)) throw new ConsoleApiError('invalid_response');
  return value;
}
function nullableDecimal(value: unknown): string | null { return value === null ? null : decimal(value); }
function wallet(value: unknown): Wallet {
  const data = object(value);
  if (data.currency !== 'USD') throw new ConsoleApiError('invalid_response');
  return { currency: 'USD', balance: decimal(data.balance), used: nullableDecimal(data.used) };
}
function account(value: unknown): Account {
  const data = object(value);
  const permissions = data.permissions === undefined ? { manageClients: false } : { manageClients: boolean(object(data.permissions).manageClients) };
  return {
    id: integer(data.id), username: text(data.username), name: text(data.name), email: text(data.email),
    status: integer(data.status), role: integer(data.role), wallet: wallet(data.wallet), permissions,
  };
}
function client(value: unknown): Client {
  const data = object(value);
  if (data.source !== 'created' && data.source !== 'existing') throw new ConsoleApiError('invalid_response');
  return { id: integer(data.id), username: text(data.username), name: text(data.name), createdAt: integer(data.createdAt), createdBy: data.createdBy === null ? null : integer(data.createdBy), source: data.source };
}
function clientOperation(value: unknown): ClientOperation {
  const data = object(value);
  if (!['prepared', 'dispatching', 'created_unresolved', 'complete', 'conflict', 'uncertain'].includes(String(data.state))) throw new ConsoleApiError('invalid_response');
  const attemptId = data.attemptId === null ? null : text(data.attemptId);
  if (attemptId !== null && !operationIdPattern.test(attemptId)) throw new ConsoleApiError('invalid_response');
  return { id: text(data.id), username: text(data.username), name: text(data.name), state: data.state as ClientOperation['state'], createdAt: integer(data.createdAt), updatedAt: integer(data.updatedAt), clientId: data.clientId === null ? null : integer(data.clientId), attemptId };
}
function clientCreation(value: unknown): ClientCreationResult {
  const data = object(value);
  const operation = clientOperation(data.operation);
  const result = { operation, ...(data.client === undefined ? {} : { client: client(data.client) }) };
  if (operation.state === 'complete' && (!result.client || result.client.id !== operation.clientId || result.client.username !== operation.username)) throw new ConsoleApiError('invalid_response');
  return result;
}
const operationIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function key(value: unknown): KeySummary {
  const data = object(value);
  const maskedKey = text(data.maskedKey);
  if (!maskedKey.includes('*') && !maskedKey.includes('•') && !maskedKey.includes('…')) throw new ConsoleApiError('invalid_response');
  return {
    id: integer(data.id), name: text(data.name), maskedKey, status: integer(data.status),
    createdAt: integer(data.createdAt), lastUsedAt: integer(data.lastUsedAt), expiresAt: integer(data.expiresAt),
    unlimitedQuota: boolean(data.unlimitedQuota), remainingUsd: nullableDecimal(data.remainingUsd), usedUsd: nullableDecimal(data.usedUsd),
    modelLimitsEnabled: boolean(data.modelLimitsEnabled), modelLimits: text(data.modelLimits),
    allowIps: data.allowIps === null ? null : text(data.allowIps),
  };
}
function record(value: unknown): UsageRecord {
  const data = object(value);
  return {
    id: integer(data.id), createdAt: integer(data.createdAt), type: integer(data.type),
    modelName: text(data.modelName), tokenName: text(data.tokenName), requestId: text(data.requestId),
    promptTokens: integer(data.promptTokens), completionTokens: integer(data.completionTokens),
    durationSeconds: integer(data.durationSeconds), streaming: boolean(data.streaming), amountUsd: nullableDecimal(data.amountUsd),
    quota: data.quota == null ? null : (() => { const value = integer(data.quota); if (value < 0) throw new ConsoleApiError('invalid_response'); return value; })(),
  };
}
function page<T>(value: unknown, project: (value: unknown) => T): Page<T> {
  const data = object(value);
  if (!Array.isArray(data.items)) throw new ConsoleApiError('invalid_response');
  const result = { items: data.items.map(project), page: integer(data.page), pageSize: integer(data.pageSize), total: integer(data.total) };
  if (result.page < 1 || result.pageSize < 1 || result.pageSize > 100 || result.total < 0 || result.items.length > result.pageSize) throw new ConsoleApiError('invalid_response');
  return result;
}
function pagination(pageNumber: number, pageSize: number): URLSearchParams {
  if (!Number.isSafeInteger(pageNumber) || pageNumber < 1 || !Number.isSafeInteger(pageSize) || pageSize < 1 || pageSize > 100) throw new ConsoleApiError('invalid_input', 400);
  return new URLSearchParams({ page: String(pageNumber), pageSize: String(pageSize) });
}
function filtersQuery(filters: UsageFilters): URLSearchParams {
  const query = new URLSearchParams();
  for (const name of ['start', 'end'] as const) {
    const value = filters[name];
    if (value !== undefined) {
      if (!Number.isSafeInteger(value) || value < 1) throw new ConsoleApiError('invalid_input', 400);
      query.set(name, String(value));
    }
  }
  if (filters.start !== undefined && filters.end !== undefined && filters.start > filters.end) throw new ConsoleApiError('invalid_input', 400);
  for (const name of ['model', 'key'] as const) {
    const value = filters[name];
    if (value !== undefined) {
      if (typeof value !== 'string' || value.length > 200 || /[\u0000-\u001f\u007f]/.test(value)) throw new ConsoleApiError('invalid_input', 400);
      query.set(name, value);
    }
  }
  return query;
}

/** Browser credentials remain in HttpOnly cookies; the CSRF token lives only in this instance. */
export function createConsoleService(options: { fetch?: typeof fetch } = {}): ConsoleService {
  const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
  let csrfToken: string | null = null;
  let sessionGeneration = 0;

  async function request(path: string, method = 'GET', body?: Record<string, string>, login = false, generation = sessionGeneration): Promise<unknown> {
    const mutation = method !== 'GET';
    if (mutation && !login && !csrfToken) throw new ConsoleApiError('session_required', 401);
    const headers = new Headers({ Accept: 'application/json' });
    if (body) headers.set('Content-Type', 'application/json');
    if (mutation && !login && csrfToken) headers.set('X-CSRF-Token', csrfToken);
    let response: Response;
    try {
      response = await fetcher(`/api/console${path}`, {
        method, headers, credentials: 'same-origin', cache: 'no-store', redirect: 'error',
        signal: AbortSignal.timeout(path === '/keys' && method === 'POST' ? 45_000 : 35_000), body: body ? JSON.stringify(body) : undefined,
      });
    } catch {
      // A lost response can follow a completed mutation. Never repeat it automatically.
      throw new ConsoleApiError(mutation && !login ? 'mutation_uncertain' : 'network_error');
    }
    if (response.status === 401 && !login && generation === sessionGeneration) csrfToken = null;
    let payload: unknown;
    try { payload = await response.json(); } catch {
      throw new ConsoleApiError(mutation && !login ? 'mutation_uncertain' : 'invalid_response', response.status);
    }
    if (!response.ok) {
      const data = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
      const error = data.error && typeof data.error === 'object' ? data.error as Record<string, unknown> : {};
      const reportedCode = typeof error.code === 'string' ? error.code : 'request_failed';
      const code = mutation && !login && response.status >= 500 && !Object.hasOwn(messages, reportedCode) ? 'mutation_uncertain' : reportedCode;
      if (code === 'customer_access_denied' && generation === sessionGeneration) csrfToken = null;
      throw new ConsoleApiError(code, response.status);
    }
    return payload;
  }
  function session(value: unknown, generation: number): Session {
    const data = object(value);
    const next = text(data.csrfToken);
    if (!next) throw new ConsoleApiError('invalid_response');
    const result = { account: account(data.account), csrfToken: next };
    if (generation === sessionGeneration) csrfToken = next;
    return result;
  }

  return {
    async readSession() {
      const generation = ++sessionGeneration;
      return session(await request('/session', 'GET', undefined, false, generation), generation);
    },
    async login(username, password) {
      const generation = ++sessionGeneration;
      return session(await request('/session', 'POST', { username, password }, true, generation), generation);
    },
    async logout() {
      const generation = ++sessionGeneration;
      await request('/session', 'DELETE', undefined, false, generation);
      if (generation === sessionGeneration) csrfToken = null;
    },
    async getAccount() { return account(object(await request('/account')).account); },
    async getWallet() { return wallet(object(await request('/wallet')).wallet); },
    async listKeys(pageNumber = 1, pageSize = 20) { return page(await request(`/keys?${pagination(pageNumber, pageSize)}`), key); },
    async createKey(name) {
      const clean = name.trim();
      if (!clean || new TextEncoder().encode(clean).byteLength > 50 || /[\u0000-\u001f\u007f]/.test(clean)) throw new ConsoleApiError('invalid_input', 400);
      const result = await request('/keys', 'POST', { name: clean });
      // A valid response is returned directly, before any optional page refresh.
      // If it cannot be read, the create result remains uncertain and must not be repeated.
      try {
        const data = object(result);
        const secret = text(data.secret);
        if (!secret || secret.includes('*')) throw new ConsoleApiError('invalid_response');
        return { key: key(data.key), secret };
      } catch { throw new ConsoleApiError('key_creation_incomplete'); }
    },
    async revokeKey(id) {
      if (!Number.isSafeInteger(id) || id < 1) throw new ConsoleApiError('invalid_input', 400);
      await request(`/keys/${id}`, 'DELETE');
    },
    async usageSummary(filters = {}) {
      const data = object(await request(`/usage/summary?${filtersQuery(filters)}`));
      return { amountUsd: nullableDecimal(data.amountUsd), quota: integer(data.quota), rpm: integer(data.rpm), tpm: integer(data.tpm) };
    },
    async usageHistory(filters = {}, pageNumber = 1, pageSize = 20) {
      const query = filtersQuery(filters);
      for (const [name, value] of pagination(pageNumber, pageSize)) query.set(name, value);
      return page(await request(`/usage/history?${query}`), record);
    },
    async listClients(pageNumber = 1, pageSize = 20) { return page(await request(`/staff/clients?${pagination(pageNumber, pageSize)}`), client); },
    async createClient(input) {
      const username = input.username.trim();
      const name = input.name.trim();
      if (!operationIdPattern.test(input.operationId) || !operationIdPattern.test(input.attemptId) || !/^[A-Za-z0-9][A-Za-z0-9._-]{2,31}$/.test(username) || !name || [...name].length > 20 || /[\p{Cc}\p{Cs}]/u.test(name) || typeof input.password !== 'string' || input.password.length < 12 || input.password.length > 17 || /[^\x21-\x7e]/.test(input.password)) throw new ConsoleApiError('invalid_input', 400);
      const result = await request('/staff/clients', 'POST', { operationId: input.operationId, attemptId: input.attemptId, username, name, password: input.password });
      try { return clientCreation(result); } catch { throw new ConsoleApiError('client_creation_uncertain'); }
    },
    async listClientOperations(pageNumber = 1, pageSize = 20) { return page(await request(`/staff/client-operations?${pagination(pageNumber, pageSize)}`), clientOperation); },
    async getClientOperation(id) {
      if (!operationIdPattern.test(id)) throw new ConsoleApiError('invalid_input', 400);
      return clientCreation(await request(`/staff/client-operations/${id}`));
    },
  };
}
